'use client';

import React, { useMemo, useRef } from 'react';
import LeavePreviewButton from 'components/LeavePreviewButton';
import MainContainer from 'layout/src/components/MainContainer';
import SidePops from 'layout/src/components/SidePops';
import useActiveHeading from 'hooks/useActiveHeading';

import Image from 'app/components/Image';
import PostBody from 'app/components/PostBody';
import PostAuthor from 'app/components/author/PostAuthor';
import StartHere from 'app/components/StartHere';
import TopicsNavigation from 'components/TopicsNavigation';
import ProgressBarWrapper from './ProgressBar';
import ArticleHero from './ArticleHero';
import ArticleToc from './ArticleToc';
import ArticleFaq from './ArticleFaq';
import HiringCard from './HiringCard';
import { type HandlePageDataType } from '../data/handlePageData';

/** Page side margins from the handoff: 16px up to 833px, 24px at 834px, 32px from 1024px. */
const ARTICLE_CONTAINER = 'mdS:mx-4 md:mx-6 mdL:mx-8';

const COVER_SIZES =
  '(min-width: 1920px) 53rem, (min-width: 1728px) 54rem, (min-width: 1280px) 48rem, (min-width: 1024px) 38rem, (min-width: 834px) 32.25rem, (min-width: 640px) 38rem, calc(100vw - 2rem)';

export type Props = Pick<
  HandlePageDataType,
  | 'post'
  | 'topics'
  | 'breadcrumbsItems'
  | 'moreInSubcategory'
  | 'authorTaxonomies'
  | 'tocItems'
  | 'hideAuthor'
  | 'hideSharing'
  | 'preview'
  | 'errors'
  | 'url'
  | 'faqs'
  | 'formattedDateModified'
  | 'isoDatePublished'
  | 'bannerCta'
  | 'sidePopsData'
>;

export default function SlugPageComponent({
  post,
  topics,
  authorTaxonomies,
  tocItems,
  hideAuthor,
  hideSharing,
  preview,
  errors,
  url,
  breadcrumbsItems,
  moreInSubcategory,
  faqs,
  formattedDateModified,
  isoDatePublished,
  bannerCta,
  sidePopsData,
}: Props) {
  const articleRef = useRef<HTMLElement>(null);
  const tocIds = useMemo(() => tocItems.map((item) => item.id), [tocItems]);
  const { activeId, setActiveId } = useActiveHeading(tocIds);

  const author = hideAuthor ? null : post.author?.node;
  const coverImage = post.featuredImage?.node;

  return (
    <>
      {preview && <LeavePreviewButton />}
      <ProgressBarWrapper articleRef={articleRef} />
      <SidePops {...sidePopsData} />

      <main id="main-content" className="bg-site-neutrals-25 [--article-sticky-top:7rem]">
        <div className="from-site-neutrals-0 to-site-neutrals-25 bg-gradient-to-b">
          <MainContainer className={ARTICLE_CONTAINER}>
            <ArticleHero
              title={post.title ?? ''}
              excerpt={post.excerpt}
              breadcrumbsItems={breadcrumbsItems}
              formattedDateModified={formattedDateModified}
              dateTimeModified={isoDatePublished}
              timeToRead={post.timeToRead}
              preview={preview}
              errors={errors}
            />
          </MainContainer>
        </div>

        <MainContainer className={ARTICLE_CONTAINER}>
          <div className="mdL:grid-cols-[minmax(0,1fr)_20rem] mdL:gap-x-8 lgS:gap-x-32 grid grid-cols-1 gap-y-6 pb-16 pt-14 md:grid-cols-[minmax(0,1fr)_15.4375rem] md:gap-x-6 md:pb-32 2xl:grid-cols-[minmax(0,1fr)_25rem] 2xl:gap-x-64">
            <article ref={articleRef} className="flex min-w-0 flex-col gap-8">
              {author && <PostAuthor author={author} taxonomies={authorTaxonomies} />}

              {coverImage?.mediaDetails?.file && (
                <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                  <Image
                    src={coverImage.mediaDetails.file}
                    alt={coverImage.altText ?? ''}
                    fill
                    preload
                    sizes={COVER_SIZES}
                    className="object-cover"
                    loading="eager"
                  />
                </div>
              )}

              <ArticleToc items={tocItems} activeId={activeId} onSelect={setActiveId} className="md:hidden" />

              <PostBody
                content={post.content}
                title={post.title ?? 'BairesDev Blog'}
                url={url}
                hideSharing={hideSharing}
                bannerCta={bannerCta}
              />

              {faqs && <ArticleFaq {...faqs} className="mdS:ml-[4.5rem]" />}

              {author && (
                <PostAuthor
                  author={author}
                  taxonomies={authorTaxonomies}
                  showBadge={false}
                  stackDetails
                  classes="mdS:ml-[4.5rem]"
                />
              )}
            </article>

            <aside
              aria-label="Article sidebar"
              className="mdS:pl-[4.5rem] flex flex-col gap-12 md:justify-between md:pl-0"
            >
              {/* The Hiring card sticks within this block, so it stops where "More in" begins */}
              <div className="flex flex-col gap-12 md:flex-1">
                <ArticleToc
                  items={tocItems}
                  activeId={activeId}
                  onSelect={setActiveId}
                  className="hidden md:flex"
                />
                <div className="md:sticky md:top-[var(--article-sticky-top)]">
                  <HiringCard />
                </div>
              </div>

              {topics && <TopicsNavigation {...topics} />}
            </aside>
          </div>
        </MainContainer>

        {moreInSubcategory && (
          <MainContainer className={ARTICLE_CONTAINER}>
            <StartHere data={moreInSubcategory} className="lgS:pb-32 pb-16" />
          </MainContainer>
        )}
      </main>
    </>
  );
}
